import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlantProvider } from "@/context/PlantContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import PublicAgreement from "@/pages/PublicAgreement";
import VaultEntrance from "@/pages/VaultEntrance";
import { hasAccess } from "@/lib/accessKey";
import ResetPassword from "@/pages/ResetPassword";
import AppShell from "@/components/layout/AppShell";
import Dashboard from "@/pages/Dashboard";
import Organizations from "@/pages/Organizations";
import Clients from "@/pages/Clients";
import WarRoomRedirect from "@/components/WarRoomRedirect";
import { presentingKey } from "@/lib/showcaseTour";
import DemoDriver from "@/pages/DemoDriver";
import SlumpLog from "@/pages/SlumpLog";
import PourWarnings from "@/pages/PourWarnings";
import SupportInbox from "@/pages/SupportInbox";
import Command from "@/pages/Command";
import Batching from "@/pages/Batching";
import Batcher from "@/pages/Batcher";
import Customers from "@/pages/Customers";
import Pricing from "@/pages/Pricing";
import Surcharges from "@/pages/Surcharges";
import Mixes from "@/pages/Mixes";
import Materials from "@/pages/Materials";
import MassManagement from "@/pages/MassManagement";
import Fleet from "@/pages/Fleet";
import Inventory from "@/pages/Inventory";
import Dockets from "@/pages/Dockets";
import Invoices from "@/pages/Invoices";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import DriverPortal from "@/pages/DriverPortal";
import DriverApp from "@/pages/DriverApp";
import DriverPinLogin from "@/pages/DriverPinLogin";
import LoaderScreen from "@/pages/LoaderScreen";
import ArmorBatchLink from "@/pages/ArmorBatchLink";
import MapPage from "@/pages/Map";
import Plants from "@/pages/Plants";
import CashSales from "@/pages/CashSales";
import PayPage from "@/pages/Pay";
import PublicDocket from "@/pages/PublicDocket";
import PourResponse from "@/pages/PourResponse";
import LiveDispatch from "@/pages/LiveDispatch";
import Drivers from "@/pages/Drivers";
import Executive from "@/pages/Executive";
import Maintenance from "@/pages/Maintenance";
import Statements from "@/pages/Statements";
import Planner from "@/pages/Planner";
import SalesSchedule from "@/pages/SalesSchedule";
import SystemLogs from "@/pages/SystemLogs";
import DailySummary from "@/pages/DailySummary";
import FleetHealth from "@/pages/FleetHealth";
import WarRoom from "@/pages/WarRoom";
import Showcase from "@/pages/Showcase";
import FeatureShowcase from "@/pages/FeatureShowcase";
import Flyer from "@/pages/Flyer";
import PrintCentre from "@/pages/PrintCentre";
import CustomerTracker from "@/pages/CustomerTracker";
import PumpTracker from "@/pages/PumpTracker";
import PumpCompanies from "@/pages/PumpCompanies";
import Haulage from "@/pages/Haulage";
import PortalRequests from "@/pages/PortalRequests";
import Leads from "@/pages/Leads";
import PublicTour from "@/pages/PublicTour";
import Signup from "@/pages/Signup";
import TrialExpired from "@/pages/TrialExpired";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentReturn";
import { PortalAuthProvider, usePortalAuth } from "@/context/PortalAuthContext";
import PortalShell from "@/components/layout/PortalShell";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalRegister from "@/pages/portal/PortalRegister";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalPlaceOrder from "@/pages/portal/PortalPlaceOrder";
import PortalJobs from "@/pages/portal/PortalJobs";
import PortalDockets from "@/pages/portal/PortalDockets";
import PortalInvoices from "@/pages/portal/PortalInvoices";
import PortalAccount from "@/pages/portal/PortalAccount";
import PortalQuotes from "@/pages/portal/PortalQuotes";
import PortalContact from "@/pages/portal/PortalContact";
import { ContractorAuthProvider, useContractorAuth } from "@/context/ContractorAuthContext";
import ContractorShell from "@/components/layout/ContractorShell";
import ContractorLogin from "@/pages/contractor/ContractorLogin";
import ContractorFleet from "@/pages/contractor/ContractorFleet";
import ContractorTrucks from "@/pages/contractor/ContractorTrucks";
import ContractorLoads from "@/pages/contractor/ContractorLoads";
import ContractorAccount from "@/pages/contractor/ContractorAccount";
import PartnerSuppliers from "@/pages/contractor/PartnerSuppliers";
import PartnerInvites from "@/pages/contractor/PartnerInvites";
import PartnerPumps from "@/pages/contractor/PartnerPumps";
import PartnerJobs from "@/pages/contractor/PartnerJobs";
import LiteCockpit from "@/pages/LiteCockpit";
import LiteDrive from "@/pages/LiteDrive";
import PartnerInviteLanding from "@/pages/PartnerInviteLanding";
import TruckTracker from "@/pages/TruckTracker";
import TruckQrSheet from "@/pages/TruckQrSheet";
import Partners from "@/pages/Partners";

const ContractorProtected = ({ children }) => {
  const { user, loading } = useContractorAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0D10]" data-testid="contractor-loading">
        <div className="w-12 h-12 bg-[#38BDF8] animate-pulse" />
      </div>
    );
  if (user === false) return <Navigate to="/contractor/login" replace />;
  return children;
};

const PortalProtected = ({ children }) => {
  const { user, loading } = usePortalAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0b]" data-testid="portal-loading">
        <div className="w-12 h-12 rounded bg-primary-orange animate-pulse" />
      </div>
    );
  if (user === false) return <Navigate to="/portal/login" replace />;
  return children;
};

const Protected = ({ children }) => {
  const { user, org, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center splash-v2" data-testid="app-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-primary-orange flex items-center justify-center shadow-2xl shadow-orange-900/40 pulse-orange">
            <div className="w-6 h-6 rounded-sm bg-black" />
          </div>
          <div className="label-tag text-primary-orange">LOADING ARMORBATCH GLOBAL</div>
        </div>
      </div>
    );
  if (user === "trial_expired") return <TrialExpired />;
  // The vault door is the mandatory front door for the public. Only devices that arrived on
  // a private access link (/login?key=…) go straight to the login screen.
  if (user === false) {
    // Drivers sign in with a PIN, not email — send them to the PIN pad.
    if (window.location.pathname.startsWith("/driver")) return <Navigate to="/driver/login" replace />;
    return <Navigate to={hasAccess() ? "/login" : "/welcome"} replace />;
  }
  // Demo guests roam the whole sample plant read-only, but never the platform-admin screens.
  const DEMO_BLOCKED = ["/organizations", "/system-logs", "/support-inbox", "/leads", "/settings/link"];
  if (user.demo_only && DEMO_BLOCKED.includes(window.location.pathname)) return <Navigate to="/war-room" replace />;
  // A plant that joined through its pump company's invite gets the one-page Lite Cockpit
  // and nothing else — no dispatch board, no menus.
  if (org?.plan === "free_dispatch" && window.location.pathname !== "/lite") return <Navigate to="/lite" replace />;
  return children;
};

// Drivers are locked to the driver portal — anything else redirects them there.
const StaffOnly = ({ children }) => {
  const { user } = useAuth();
  if (user && user.role === "driver") return <Navigate to="/driver" replace />;
  if (user && user.role === "loader") return <Navigate to="/loader" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PortalAuthProvider>
        <ContractorAuthProvider>
        <PlantProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/sign/:token" element={<PublicAgreement />} />
            <Route path="/welcome" element={<VaultEntrance />} />
            <Route path="/tour" element={<PublicTour />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pay/:token" element={<PayPage />} />
            <Route path="/d/:token" element={<PublicDocket />} />
            <Route path="/pour/:token" element={<PourResponse />} />
            <Route path="/track/:token" element={<CustomerTracker />} />
            <Route path="/pump/:token" element={<PumpTracker />} />
            <Route path="/track/job/:token" element={<PumpTracker mode="job" />} />
            <Route path="/demo/driver/:token" element={<DemoDriver />} />
            <Route path="/driver/login" element={<DriverPinLogin />} />
            <Route path="/driver" element={<Protected><DriverApp /></Protected>} />
            <Route path="/loader" element={<Protected><LoaderScreen /></Protected>} />
            <Route path="/driver/classic" element={<Protected><DriverPortal /></Protected>} />
            <Route path="/live" element={<Protected><LiveDispatch /></Protected>} />
            <Route path="/showcase" element={<Showcase />} />
            <Route path="/explore" element={<FeatureShowcase />} />
            <Route path="/flyer" element={<Flyer />} />

            {/* Customer self-service portal */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/register" element={<PortalRegister />} />
            <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />
            <Route element={<PortalProtected><PortalShell /></PortalProtected>}>
              <Route path="/portal/dashboard" element={<PortalDashboard />} />
              <Route path="/portal/orders/new" element={<PortalPlaceOrder />} />
              <Route path="/portal/jobs" element={<PortalJobs />} />
              <Route path="/portal/dockets" element={<PortalDockets />} />
              <Route path="/portal/invoices" element={<PortalInvoices />} />
              <Route path="/portal/account" element={<PortalAccount />} />
              <Route path="/portal/quotes" element={<PortalQuotes />} />
              <Route path="/portal/contact" element={<PortalContact />} />
            </Route>

            {/* Contractor Portal — haulage contractors (own trucks, no plant) */}
            <Route path="/partner-invite/:token" element={<PartnerInviteLanding />} />
            <Route path="/truck/:token" element={<TruckTracker />} />
            <Route path="/pump-unit/:token" element={<PumpTracker mode="unit" />} />
            <Route path="/drive/:token" element={<LiteDrive />} />
            <Route path="/lite" element={<Protected><LiteCockpit /></Protected>} />
            <Route path="/contractor/login" element={<ContractorLogin />} />
            <Route path="/contractor" element={<Navigate to="/contractor/suppliers" replace />} />
            <Route element={<ContractorProtected><ContractorShell /></ContractorProtected>}>
              <Route path="/contractor/suppliers" element={<PartnerSuppliers />} />
              <Route path="/contractor/invite" element={<PartnerInvites />} />
              <Route path="/contractor/fleet" element={<ContractorFleet />} />
              <Route path="/contractor/trucks" element={<ContractorTrucks />} />
              <Route path="/contractor/pumps" element={<PartnerPumps />} />
              <Route path="/contractor/jobs" element={<PartnerJobs />} />
              <Route path="/contractor/loads" element={<ContractorLoads />} />
              <Route path="/contractor/account" element={<ContractorAccount />} />
            </Route>

            <Route element={<Protected><StaffOnly><AppShell /></StaffOnly></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/war-room" element={<WarRoom />} />
              <Route path="/dispatch" element={<WarRoomRedirect><Command /></WarRoomRedirect>} />
              <Route path="/orders" element={<Command />} />
              <Route path="/batching" element={<Batching />} />
              <Route path="/batcher" element={<Batcher />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/surcharges" element={<Surcharges />} />
              <Route path="/portal-requests" element={<PortalRequests />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/pump-companies" element={<PumpCompanies />} />
              <Route path="/haulage" element={<Haulage />} />
              <Route path="/mixes" element={<Mixes />} />
              <Route path="/materials" element={<Materials />} />
              <Route path="/mass-management" element={<MassManagement />} />
              <Route path="/fleet/qr-sheet" element={<TruckQrSheet />} />
              <Route path="/fleet" element={<Fleet />} />
              <Route path="/drivers" element={<Drivers />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/dockets" element={<Dockets />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/cashsales" element={<CashSales />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/map" element={<MapPage />} />
              <Route path="/plants" element={<Plants />} />
              <Route path="/executive" element={<Executive />} />
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/statements" element={<Statements />} />
              <Route path="/planner" element={<Planner />} />
              <Route path="/sales" element={<SalesSchedule />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/link" element={<ArmorBatchLink />} />
              <Route path="/daily-summary" element={<DailySummary />} />
              <Route path="/fleet-health" element={<FleetHealth />} />
              <Route path="/system-logs" element={<SystemLogs />} />
              <Route path="/organizations" element={<Organizations />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/slump-log" element={<SlumpLog />} />
              <Route path="/print-centre" element={<PrintCentre />} />
              <Route path="/pour-warnings" element={<PourWarnings />} />
              <Route path="/support-inbox" element={<SupportInbox />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </PlantProvider>
        </ContractorAuthProvider>
        </PortalAuthProvider>
        <Toaster position="top-right" richColors offset={presentingKey() ? 56 : undefined} />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
